"use client";
import { useEffect, useState, useRef, useCallback } from "react";
import clsx from "clsx";

import { useClientContext } from "@/shared/components/Context";

import s from "./styles.module.css";

const getIsAlternateHightlight = (chunk: string): boolean =>
  chunk[0] === "[" && chunk[chunk.length - 1] === "]";

const clearChunk = (chunk: string): string => chunk.slice(1, -1);

interface Props {
  intro?: string;
  text: string;
  className?: string;
}

export default function TextReveal({ text, intro = "", className }: Props) {
  const clientContext = useClientContext();
  const [countToHightlight, setCountToHightlight] = useState(0);
  const delimeter = useRef<string>(" ");
  const ref = useRef<HTMLElement>(null);

  const splittedTextRef = useRef(text.split(delimeter.current));
  const splittedText = splittedTextRef.current;

  const handleScroll = useCallback(() => {
    const section = ref.current;
    const scrollPosition = window.scrollY;

    if (section) {
      const [beginChunkAnimation, endChunkAnimation] = [
        clientContext.welcomeHeight - window.innerHeight / 3,
        section.offsetHeight -
          window.innerHeight / 1.4 +
          clientContext.welcomeHeight,
      ];

      const scrollPercentage =
        ((scrollPosition - beginChunkAnimation) /
          (endChunkAnimation - beginChunkAnimation)) *
        100;

      const calculatedCountToHighlight =
        (scrollPercentage / 100) * splittedText.length;

      if (calculatedCountToHighlight !== countToHightlight) {
        setCountToHightlight(Math.max(0, calculatedCountToHighlight));
      }
    }
  }, [countToHightlight, clientContext.welcomeHeight, splittedText.length]);

  useEffect(() => {
    const section = ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            window.addEventListener("scroll", handleScroll, { passive: true });
          } else {
            window.removeEventListener("scroll", handleScroll);
          }
        });
      },
      {
        rootMargin: "200px 0px 200px 0px",
      }
    );

    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [clientContext.welcomeHeight, handleScroll]);

  return (
    <section ref={ref} className={clsx(s.generalDesciption, className)}>
      {intro && <h3 className={clsx(s.intro)}>{intro}</h3>}
      <p className={s.generalDesciptionText}>
        {splittedText.map((chunk, i) => {
          const isAlternateHightlight = getIsAlternateHightlight(chunk);

          const hightlighedClassName = isAlternateHightlight
            ? s.alternateHighlightedChunk
            : s.highlightedChunk;

          const formattedChunk = isAlternateHightlight
            ? clearChunk(chunk)
            : chunk;

          return (
            <span
              key={i}
              className={clsx(s.chunk, {
                [hightlighedClassName]: i < Math.floor(countToHightlight),
              })}
            >
              {formattedChunk}
              {delimeter.current}
            </span>
          );
        })}
      </p>
    </section>
  );
}
